import { MoneyCollectOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Popover, Spin, Table } from 'antd';
import { pseudoModels } from '../../common/model';
import { Permission } from '../../common/permision';
import { getImageModelName } from '../../image/image-llm';
import { useAuthority } from '../../me/hook';
import {
  videoModelNameMap,
  videoModelSpecs,
  type VideoModelCode,
} from '../../video-model/model-spec';
import { imageCredis, videoCredis } from '../api';
import type { CreditSettingsDto, RemovalProvider } from '../model';

const priceFormat = new Intl.NumberFormat('zh-CN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getVideoModelName = (code: string) =>
  videoModelNameMap.get(code as VideoModelCode) ??
  pseudoModels.get(code) ??
  code;

/** 按次计费的伪模型，其余视频模型按秒计费（见 credit/hook.ts 的计费分支） */
const perCallModels = new Set([
  'app_video_smart_erase',
  'app_video_quality_restoration',
]);

const getVideoUnit = (model: string) =>
  perCallModels.has(model) ? '信用点/次' : '信用点/秒';

/** 伪模型的权限要求，与 side-bar 菜单入口保持一致 */
const pseudoModelPermissions = new Map<string, Permission[]>([
  ['app_video_translate', [Permission.视频翻译]],
  ['app_video_smart_erase', [Permission.智能擦除]],
  ['app_video_quality_restoration', [Permission.画质修复]],
]);

/** default 兜底价的计费单位：视频默认按秒，图片默认按张 */
const defaultUnitPerType = {
  video: '信用点/秒',
  image: '信用点/张',
} as const;

/** 计费变体的展示名，目前仅视频翻译按音画场景（简单/复杂）差异化计价 */
const variantNameMap = new Map([
  ['las', '简单场景'],
  ['heygen', '复杂场景'],
]);

const resolveVariantName = (code: string) => variantNameMap.get(code) ?? code;

const PriceSection = (props: {
  title: string;
  type: keyof typeof defaultUnitPerType;
  data: CreditSettingsDto | undefined;
  resolveName: (model: string) => string;
  resolveUnit: (model: string) => string;
  filterModel?: (model: string) => boolean;
  superRemovalProvider?: RemovalProvider;
}) => {
  const {
    title,
    type,
    data,
    resolveName,
    resolveUnit,
    filterModel,
    superRemovalProvider,
  } = props;

  const rows =
    data?.settings
      .filter((item) => filterModel?.(item.model) ?? true)
      .flatMap((item) => {
        if (item.model === 'app_video_smart_erase' && item.removal_pricing) {
          return (
            [
              ['wuhenai', '智能擦除'],
              [superRemovalProvider ?? 'kaipai', '超级擦除'],
            ] as const
          ).flatMap(([provider, name]) => {
            const pricing = item.removal_pricing?.[provider];
            return pricing
              ? [
                  {
                    key: `${item.model}:${provider}`,
                    model: item.model,
                    name,
                    credit: pricing.credit,
                    unit: pricing.unit === 'second' ? '信用点/秒' : '信用点/次',
                    variantCode: undefined as string | undefined,
                  },
                ]
              : [];
          });
        }

        // variants.default 与基础价等价，不重复展示；子行跟随模型行的计费单位
        const variantRows = Object.entries(item.variants ?? {})
          .filter(([code]) => code !== 'default')
          .map(([code, credit]) => ({
            key: `${item.model}:${code}`,
            model: item.model,
            credit,
            name: resolveVariantName(code),
            unit: resolveUnit(item.model),
            variantCode: code,
          }));

        // 有变体的模型基础行只作为分组标题，价格明细由子行展示
        const base = {
          key: item.model,
          model: item.model,
          credit: variantRows.length > 0 ? undefined : item.credit,
          name: resolveName(item.model),
          unit: variantRows.length > 0 ? undefined : resolveUnit(item.model),
          variantCode: undefined as string | undefined,
        };

        return [base, ...variantRows];
      }) ?? [];

  // 全部行都被权限过滤掉时整个小节隐藏，避免展示空表
  if (data && rows.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="mb-1 font-bold">{title}</div>
      <Table
        tableLayout="fixed"
        rowKey="key"
        size="small"
        showHeader={false}
        pagination={false}
        dataSource={rows}
        columns={[
          {
            dataIndex: 'name',
            render: (name: string, row) => (
              <div
                className={
                  row.variantCode
                    ? 'truncate pl-3 text-xs text-gray-400'
                    : 'truncate'
                }
              >
                {name}
              </div>
            ),
          },
          {
            dataIndex: 'credit',
            width: 50,
            align: 'right',
            render: (credit: number | undefined) =>
              credit == null ? null : priceFormat.format(credit),
          },
          {
            dataIndex: 'unit',
            width: 80,
            render: (unit: string | undefined) =>
              unit == null ? null : (
                <span className="text-xs text-gray-400">{unit}</span>
              ),
          },
        ]}
      ></Table>
      {data?.default != null && (
        <div className="mt-1 text-xs text-gray-400">
          未列出的模型按 {priceFormat.format(data.default)}{' '}
          {defaultUnitPerType[type]}计费
        </div>
      )}
    </div>
  );
};

export const PriceListTable = () => {
  const videoQueryResult = useQuery(videoCredis);
  const imageQueryResult = useQuery(imageCredis);
  const authority = useAuthority();

  // 与视频生成表单一致：无权限的模型不在价格表中展示
  const isVideoModelVisible = (model: string) =>
    authority(
      videoModelSpecs.get(model as VideoModelCode)?.permission ??
        pseudoModelPermissions.get(model),
    );

  // 图片模型服务于「图片生成 / 图片翻译」两个入口，任一权限即可见
  const canSeeImageModels = authority([
    Permission.图片生成,
    Permission.图片翻译,
  ]);

  return (
    <Spin spinning={videoQueryResult.isLoading || imageQueryResult.isLoading}>
      <div className="flex flex-col gap-3">
        <PriceSection
          title="视频生成"
          data={videoQueryResult.data}
          resolveName={getVideoModelName}
          resolveUnit={getVideoUnit}
          filterModel={isVideoModelVisible}
          type="video"
          superRemovalProvider={
            authority(Permission.SoundView擦除) ? 'soundview' : 'kaipai'
          }
        />
        {canSeeImageModels && (
          <PriceSection
            title="图片生成"
            data={imageQueryResult.data}
            resolveName={getImageModelName}
            resolveUnit={() => '信用点/张'}
            type="image"
          />
        )}
        {authority(Permission.视频翻译) && (
          <div className="text-xs text-gray-400">
            视频翻译包含擦除步骤时，另计对应模式的擦除费用。
          </div>
        )}
        <div className="text-xs text-gray-400">1信用点 = 0.1元</div>
      </div>
    </Spin>
  );
};

export const PriceListButton = () => {
  return (
    <Popover
      classNames={{ content: 'w-80 max-h-96 overflow-auto' }}
      placement="bottom"
      title="信用点价格表"
      content={<PriceListTable></PriceListTable>}
      destroyOnHidden
    >
      <Button icon={<MoneyCollectOutlined />} variant="filled" color="default">
        价格表
      </Button>
    </Popover>
  );
};
